import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CHATBOT } from "@/constants/testIds";
import { Send, Bot, Zap, User } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicChat() {
  const { divisionId, agentSlug, widgetSlug } = useParams();
  const [sp] = useSearchParams();
  const isEmbed = sp.get("embed") === "1";
  const [config, setConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [captureStage, setCaptureStage] = useState(null);
  const scrollRef = useRef();

  // Resolve which endpoints to hit based on the URL type
  const configUrl = widgetSlug
    ? `${BACKEND}/api/public/w/${widgetSlug}/config`
    : agentSlug
      ? `${BACKEND}/api/public/agent-chatbot/${agentSlug}/config`
      : `${BACKEND}/api/public/chatbot/${divisionId}/config`;
  const messageUrl = widgetSlug
    ? `${BACKEND}/api/public/w/${widgetSlug}/message`
    : `${BACKEND}/api/public/chatbot/message`;

  useEffect(() => {
    axios.get(configUrl).then((r) => {
      setConfig(r.data);
      setMessages([{ role: "assistant", text: r.data.welcome_message }]);
    }).catch(() => setConfig({ welcome_message: "Hi there!", suggested_questions: [], primary_color: "#007AFF" }));
  }, [configUrl]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, typing]);

  const send = async (text) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setTyping(true);
    try {
      // For slug endpoints division_id is resolved server-side. For legacy/direct-division endpoints we include it.
      const payload = {
        session_id: sessionId,
        message: text,
        agent_slug: agentSlug || null,
      };
      if (!widgetSlug) payload.division_id = config?.division?.id || divisionId;
      const { data } = await axios.post(messageUrl, payload);
      setSessionId(data.session_id);
      setCaptureStage(data.capture_stage);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, escalated: data.escalated }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble responding right now.", escalated: true }]);
    } finally {
      setTyping(false);
    }
  };

  const primary = config?.primary_color || "#007AFF";
  const fontFamily = config?.font_family && config.font_family !== "System (default)"
    ? `"${config.font_family}", system-ui, -apple-system, sans-serif`
    : undefined;
  const bubbleRadiusClass =
    config?.bubble_style === "square" ? "rounded-none"
    : config?.bubble_style === "pill" ? "rounded-2xl"
    : "rounded-sm";
  const animationClass =
    config?.greeting_animation === "slide" ? "widget-anim-slide"
    : config?.greeting_animation === "bounce" ? "widget-anim-bounce"
    : config?.greeting_animation === "none" ? ""
    : "widget-anim-fade";
  const botAvatarUrl = config?.bot_avatar_url;

  const wrapperClass = isEmbed
    ? "h-screen bg-background flex flex-col"
    : "min-h-screen bg-background grain flex items-center justify-center p-6";

  const stageHint =
    captureStage === "ask_name_reply" ? "Type your name…"
    : captureStage === "ask_email" ? "Type your email address…"
    : "Type your question…";

  const BotIcon = () => botAvatarUrl
    ? <img src={botAvatarUrl} alt="Bot" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    : <Bot className="h-3.5 w-3.5" />;

  return (
    <div className={wrapperClass} data-testid="widget-root" style={fontFamily ? { fontFamily } : undefined}>
      <style>{`
        @keyframes widgetFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes widgetSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes widgetBounce { 0% { opacity: 0; transform: scale(0.7); } 60% { opacity: 1; transform: scale(1.05); } 100% { transform: scale(1); } }
        .widget-anim-fade { animation: widgetFadeIn 0.35s ease-out; }
        .widget-anim-slide { animation: widgetSlideUp 0.32s cubic-bezier(0.22, 1, 0.36, 1); }
        .widget-anim-bounce { animation: widgetBounce 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>
      <Card className={`${isEmbed ? "flex-1 rounded-none border-0" : "w-full max-w-2xl h-[85vh] rounded-sm"} bg-card border-border flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center gap-3" style={{ background: `linear-gradient(90deg, ${primary}15, transparent)` }}>
          <div className="h-9 w-9 rounded-sm flex items-center justify-center overflow-hidden" style={{ background: primary }}>
            {botAvatarUrl
              ? <img src={botAvatarUrl} alt="Bot" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : <Bot className="h-5 w-5 text-white" />}
          </div>
          <div className="flex-1">
            <div className="font-display font-extrabold" data-testid="widget-division-name">{config?.division?.name || "Support"}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />Online</div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="widget-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} ${i === messages.length - 1 && animationClass ? animationClass : ""}`}>
              {m.role === "assistant" && (
                <div className="h-7 w-7 rounded-sm flex items-center justify-center mr-2 flex-none overflow-hidden" style={{ background: primary + "20", color: primary }}>
                  <BotIcon />
                </div>
              )}
              <div className={`max-w-[75%] p-3 ${bubbleRadiusClass} text-sm whitespace-pre-wrap ${m.role === "user" ? "text-white" : "bg-secondary/60"}`} style={m.role === "user" ? { background: primary } : {}}>
                {m.text}
                {m.escalated && <div className="text-[10px] uppercase tracking-widest mt-1.5 pt-1.5 border-t border-white/10 opacity-80">Handing off to a human agent…</div>}
              </div>
              {m.role === "user" && <div className={`h-7 w-7 ${bubbleRadiusClass} bg-secondary flex items-center justify-center ml-2 flex-none`}><User className="h-3.5 w-3.5" /></div>}
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="h-7 w-7 rounded-sm flex items-center justify-center mr-2 flex-none" style={{ background: primary + "20", color: primary }}><Bot className="h-3.5 w-3.5" /></div>
              <div className="p-3 rounded-sm bg-secondary/60 flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse" />
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          )}
        </div>

        {/* Suggested questions — only for the very first turn */}
        {messages.length === 1 && (config?.suggested_questions || []).length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="widget-suggested">
            {config.suggested_questions.map((q, i) => (
              <button key={i} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-sm border border-border hover:bg-secondary/40" data-testid={`widget-suggested-${i}`}>{q}</button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-border flex gap-2">
          <Input
            className="rounded-sm bg-secondary/60"
            placeholder={stageHint}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send(input)}
            data-testid={CHATBOT.messageInput}
          />
          <Button onClick={() => send(input)} disabled={!input.trim() || typing} className="rounded-sm" style={{ background: primary }} data-testid={CHATBOT.sendMessage}><Send className="h-4 w-4" /></Button>
        </div>

        {!isEmbed && <div className="text-[10px] uppercase tracking-widest text-center text-muted-foreground p-2 border-t border-border flex items-center justify-center gap-1"><Zap className="h-3 w-3" /> Powered by LeadifyCRM</div>}
      </Card>
    </div>
  );
}
